/**
 * Resource name label with type icon for appointment allocations (calendar tags, details).
 */

import React from 'react';
import { Space, Tag, theme } from 'antd';
import { useGetResourceQuery, type ResourceType } from '@/api/mcp-appointments';
import { ResourceTypeIcon } from '@/components/resources/ResourceTypeIcon';

interface ResourceAllocationTagProps {
  resourceId: string;
  name: string;
  /** When known (e.g. from calendar resource list), skips per-tag fetch. */
  resourceType?: ResourceType;
  className?: string;
  suffix?: React.ReactNode;
  /** Icon uses parent text color instead of per-type theme colors. */
  neutralIcon?: boolean;
}

export const ResourceAllocationTag: React.FC<ResourceAllocationTagProps> = ({
  resourceId,
  name,
  resourceType: resourceTypeProp,
  className,
  suffix,
  neutralIcon
}) => {
  const { token } = theme.useToken();
  const { data: resource } = useGetResourceQuery(resourceId, !resourceTypeProp);
  const resourceType = resourceTypeProp ?? resource?.type;

  return (
    <Tag className={className} style={{ margin: 0, display: 'inline-flex', alignItems: 'center' }}>
      <Space size={4} style={{ maxWidth: '100%' }}>
        {resourceType ? (
          <ResourceTypeIcon
            type={resourceType}
            neutral={neutralIcon}
            style={{ fontSize: token.fontSizeSM }}
          />
        ) : null}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        {suffix}
      </Space>
    </Tag>
  );
};

interface ResourceTypeIconInlineProps {
  resourceId: string;
  resourceType?: ResourceType;
  style?: React.CSSProperties;
  neutral?: boolean;
}

/** Compact type icon for a resource id (calendar headlines, detail rows). */
export const ResourceTypeIconInline: React.FC<ResourceTypeIconInlineProps> = ({
  resourceId,
  resourceType: resourceTypeProp,
  style,
  neutral
}) => {
  const { data: resource } = useGetResourceQuery(resourceId, !resourceTypeProp);
  const resourceType = resourceTypeProp ?? resource?.type;
  if (!resourceType) {
    return null;
  }
  return <ResourceTypeIcon type={resourceType} neutral={neutral} style={style} />;
};
